import { defineStore } from 'pinia';
import type {
  ActionBarCrossSellAttribution,
  ActionBarCrossSellAttributionOptions,
  ActionBarMainProductType
} from '~/utils/actionBarCrossSellAnalytics/types';
import {
  ACTION_BAR_CROSS_SELL_ITEM_LIST_ID,
  ACTION_BAR_CROSS_SELL_ITEM_LIST_NAME
} from '~/utils/actionBarCrossSellAnalytics/types';

export const useActionBarCrossSellAnalyticsStore = defineStore(
  'actionBarCrossSellAnalytics',
  () => {
    const sessionId = ref<string | null>(null);
    const cartId = ref<string | null>(null);
    const mainProductSku = ref<string | null>(null);
    const mainProductType = ref<ActionBarMainProductType | null>(null);
    const attributionBySku = ref<Record<string, ActionBarCrossSellAttribution>>(
      {}
    );

    function startSession(params: {
      sessionId: string;
      cartId?: string;
      mainProductSku: string;
      mainProductType: ActionBarMainProductType;
    }): void {
      sessionId.value = params.sessionId;
      cartId.value = params.cartId ?? null;
      mainProductSku.value = params.mainProductSku;
      mainProductType.value = params.mainProductType;
    }

    function resetSession(): void {
      sessionId.value = null;
      cartId.value = null;
      mainProductSku.value = null;
      mainProductType.value = null;
    }

    function registerAttribution(
      sku: string,
      options: ActionBarCrossSellAttributionOptions
    ): void {
      attributionBySku.value = {
        ...attributionBySku.value,
        [sku]: {
          ab_session_id: options.ab_session_id,
          ab_category: options.ab_category,
          ab_result_position: options.ab_result_position,
          ab_main_product_sku: options.ab_main_product_sku,
          item_list_id: ACTION_BAR_CROSS_SELL_ITEM_LIST_ID,
          item_list_name: ACTION_BAR_CROSS_SELL_ITEM_LIST_NAME,
          selected_via_action_bar: true
        }
      };
    }

    function getAttribution(
      sku: string
    ): ActionBarCrossSellAttribution | undefined {
      return attributionBySku.value[sku];
    }

    function hasAttributionInCart(skus: string[]): boolean {
      return skus.some((sku) => attributionBySku.value[sku] != null);
    }

    return {
      sessionId: computed(() => sessionId.value),
      cartId: computed(() => cartId.value),
      mainProductSku: computed(() => mainProductSku.value),
      mainProductType: computed(() => mainProductType.value),
      attributionBySku: computed(() => attributionBySku.value),
      startSession,
      resetSession,
      registerAttribution,
      getAttribution,
      hasAttributionInCart
    };
  }
);
